type FileType = 'video' | 'image' | 'audio' | 'pdf' | 'zip' | 'unknown';

/**
 * Every extension this app treats as a video container.
 *
 * Lives in this leaf module, not in features/media, so the direction of the dependency matches
 * the layering: `media.utils` consumes this list, and nothing that needs a file-type question has
 * to pull MediaClient and the lib/utils barrel into its chunk to ask one. It also keeps the list
 * and the ambiguity set below in the same file, where they have to be edited together — split
 * across modules, adding an entry there silently widens the extension fallback here.
 */
const videoExtensions = [
  'avi',
  'mp4',
  'mov',
  'mkv',
  'webm',
  'wmv',
  'flv',
  '3gp',
  'mpg',
  'mpeg',
  'm4v',
  'm2v',
  'mxf',
  'ts',
  'vob',
  'ogv',
  'ogg',
  'ogx',
  'rm',
  'rmvb',
  'asf',
  'dv',
  'divx',
  'xvid',
  'mts',
];

const hasVideoExtension = (extension: string): boolean => videoExtensions.includes(extension);

const getExtension = (fileName: string) => {
  return fileName.toLowerCase().split('.').pop() ?? '';
};

const isVideoMime = (mimeType: string) => {
  return mimeType.startsWith('video/');
};

const isImageMime = (mimeType: string) => {
  return mimeType.startsWith('image/');
};

const isAudioMime = (mimeType: string) => {
  return mimeType.startsWith('audio/');
};

const isPdfMime = (mimeType: string) => {
  return mimeType === 'application/pdf';
};

const isZipMime = (mimeType: string) => {
  return ['application/zip', 'application/x-7z-compressed'].includes(mimeType);
};

/**
 * Extensions in the shared video list that are NOT reliably video.
 *
 * 'ogg'/'ogx' are the generic Xiph containers (audio far more often than video), '.ts' is
 * MPEG-TS but also TypeScript, and '.rm' RealMedia is both. They are correct to keep in the
 * list a video PLAYER consults, and wrong to accept as evidence that an untyped file is a
 * video — which is what a media grid is doing when it falls back to the extension.
 */
const AMBIGUOUS_VIDEO_EXTENSIONS: ReadonlySet<string> = new Set(['ogg', 'ogx', 'ts', 'rm']);

/**
 * Is this file a video? Mime type if it says so, otherwise any name the row carries.
 *
 * A `video/*` mime is proof. Anything else is not disproof — it just leaves the name as the
 * only evidence, which matters because this app's media paths routinely produce
 * `application/octet-stream`, and rows do turn up mislabelled (`audio/mp4` on a clip.mp4).
 *
 * What keeps that from over-matching is AMBIGUOUS_VIDEO_EXTENSIONS, not the mime: 'ogg', 'ts'
 * and 'rm' belong in the list a video PLAYER consults and are worthless as evidence that an
 * untyped file is a video, so an `audio/ogg` voiceover named voiceover.ogg is still rejected
 * while a clip.mp4 is accepted. Judging by mime kind instead — refusing to read the name once
 * the mime said "audio" — dropped real videos from the grid, and a dropped video is
 * indistinguishable from an empty library.
 *
 * Lives here rather than in a component so the next caller inherits the rule: media.utils'
 * `decorateMedia` and both MediaCard variants each hand-roll their own version of this test,
 * and all three have the over-matching problem above. Migrating them is a separate change.
 */
const isVideoFileLike = (mimeType: unknown, ...fileNames: unknown[]): boolean => {
  if (typeof mimeType === 'string' && isVideoMime(mimeType.trim().toLowerCase())) return true;

  // A non-video mime does NOT veto the name, it just fails to prove the case on its own. An
  // `audio/mp4` row named clip.mp4 is a mislabelled video and the old test included it; refusing
  // to look dropped it from the grid, which is indistinguishable from an empty library. The
  // ambiguity set below is what keeps this honest in the other direction: an `audio/ogg`
  // voiceover named voiceover.ogg is still rejected, because 'ogg' is not evidence of video.

  // Every name the row carries, not just the first. A media row can hold both `file_name` and
  // `name`, and whichever the caller happens to prefer may be the one without the extension —
  // giving up on it drops a real video from the grid, which is indistinguishable from an empty
  // library and sends a hand-off through its whole window for a save that succeeded.
  return fileNames.some((fileName) => {
    // `getExtension` returns the whole basename for a dot-less name (split('.').pop() on 'mp4'
    // is 'mp4'), so a file literally named 'mp4' would read as a video. Require a dot before
    // trusting the name at all.
    const name = typeof fileName === 'string' ? fileName : '';
    if (!name.includes('.')) return false;

    const extension = getExtension(name);
    return (
      extension !== '' && !AMBIGUOUS_VIDEO_EXTENSIONS.has(extension) && hasVideoExtension(extension)
    );
  });
};

const getFileTypeByMime = (mimeType: string): FileType => {
  if (isVideoMime(mimeType)) {
    return 'video';
  } else if (isImageMime(mimeType)) {
    return 'image';
  } else if (isAudioMime(mimeType)) {
    return 'audio';
  } else if (isPdfMime(mimeType)) {
    return 'pdf';
  } else if (isZipMime(mimeType)) {
    return 'zip';
  } else {
    return 'unknown';
  }
};

const formatSize = (bytes : number): string => {
  if (!bytes) {
    return '0 Bytes';
  }
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(1000));

  return `${parseFloat((bytes / Math.pow(1000, i)).toFixed(0))} ${sizes[i]}`;
}

export type { FileType };
export {
  getExtension,
  isVideoFileLike,
  videoExtensions,
  isVideoMime,
  isImageMime,
  isAudioMime,
  isPdfMime,
  isZipMime,
  getFileTypeByMime,
  formatSize,
};
